import React, { useState, useRef } from 'react';
import { 
  Car, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, 
  Upload, Image, XCircle, Maximize2, X, FileText, 
  ExternalLink, Loader2, Eye, Download, Edit2, Trash2, Plus, 
  Check, Save, Calendar, Star, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ASSET_CONFIGS, getActualValue } from './assetConfigs';

const VehicleDetailModal = React.memo(({ 
  vehicle, 
  vehicles, 
  currentIndex, 
  onClose, 
  onSave, 
  onNavigate,
  formatCurrency,
  API_URL,
  userEmail,
  isNewItem = false
}) => {
  // For non-market assets, value is in currentPrice (shares is always 1)
  // Check both currentPrice and if shares > 1, use shares (for legacy data)
  const getActualValue = (v) => {
    if (v.currentPrice && v.currentPrice > 0) return v.currentPrice;
    if (v.shares && v.shares > 1) return v.shares;
    return v.currentPrice || 0;
  };
  
  const actualValue = getActualValue(vehicle);
  const actualCost = vehicle.costBasis || 0;
  
  const [editForm, setEditForm] = useState({ 
    ...vehicle,
    // Use 'shares' field in form to store the actual value for editing
    shares: actualValue
  });
  const [photos, setPhotos] = useState(vehicle.photos || []);
  const [maintenanceLog, setMaintenanceLog] = useState(vehicle.maintenanceLog || []);
  const [newMaintenance, setNewMaintenance] = useState({ date: '', mileage: '', repair: '', cost: '' });
  const [editingMaintenanceId, setEditingMaintenanceId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed, number = photo index
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState(vehicle.documents || []);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);
  const [viewingDocument, setViewingDocument] = useState(null); // For PDF viewer modal
  
  // Section collapse states - persist across vehicle navigation and sessions
  // Load saved preferences from localStorage on mount
  const getInitialCollapseState = () => {
    try {
      const saved = localStorage.getItem('vehicleModalCollapseState');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading collapse state:', e);
    }
    // Default state
    return {
      details: true,
      photos: true,
      documents: false,
      maintenance: false
    };
  };
  
  const [collapseState, setCollapseState] = useState(getInitialCollapseState);
  
  // Helper to update collapse state and save to localStorage
  const updateCollapseState = (key, value) => {
    setCollapseState(prev => {
      const newState = { ...prev, [key]: value };
      try {
        localStorage.setItem('vehicleModalCollapseState', JSON.stringify(newState));
      } catch (e) {
        console.error('Error saving collapse state:', e);
      }
      return newState;
    });
  };
  
  // Destructure for easier access
  const detailsExpanded = collapseState.details;
  const photosExpanded = collapseState.photos;
  const documentsExpanded = collapseState.documents;
  const maintenanceExpanded = collapseState.maintenance;
  
  const setDetailsExpanded = (val) => updateCollapseState('details', val);
  const setPhotosExpanded = (val) => updateCollapseState('photos', val);
  const setDocumentsExpanded = (val) => updateCollapseState('documents', val);
  const setMaintenanceExpanded = (val) => updateCollapseState('maintenance', val);

  // Re-initialize form when vehicle changes (navigation)
  React.useEffect(() => {
    const newValue = getActualValue(vehicle);
    setEditForm({
      ...vehicle,
      shares: newValue
    });
    setPhotos(vehicle.photos || []);
    setDocuments(vehicle.documents || []);
    setMaintenanceLog(vehicle.maintenanceLog || []);
    setNewMaintenance({ date: '', mileage: '', repair: '', cost: '' });
    setEditingMaintenanceId(null);
  }, [vehicle.id]);

  const config = ASSET_CONFIGS.VEHICLES;
  
  // Use form values for real-time display in summary
  const displayValue = editForm.shares || 0;
  const displayCost = editForm.costBasis || 0;
  const gainLoss = displayValue - displayCost;
  const gainLossPercent = displayCost > 0 ? ((displayValue - displayCost) / displayCost) * 100 : 0;

  // Maintenance log handlers
  const handleAddMaintenance = () => {
    if (!newMaintenance.date) return;
    
    const entry = {
      id: Date.now(),
      date: newMaintenance.date,
      mileage: newMaintenance.mileage ? parseInt(newMaintenance.mileage) : null,
      repair: newMaintenance.repair || 'Service',
      cost: newMaintenance.cost ? parseFloat(newMaintenance.cost) : 0
    };
    
    setMaintenanceLog(prev => [...prev, entry].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setNewMaintenance({ date: '', mileage: '', repair: '', cost: '' });
  };

  const handleUpdateMaintenance = (id) => {
    setMaintenanceLog(prev => prev.map(entry => 
      entry.id === id ? {
        ...entry,
        date: newMaintenance.date || entry.date,
        mileage: newMaintenance.mileage ? parseInt(newMaintenance.mileage) : entry.mileage,
        repair: newMaintenance.repair || entry.repair,
        cost: newMaintenance.cost ? parseFloat(newMaintenance.cost) : entry.cost
      } : entry
    ).sort((a, b) => new Date(b.date) - new Date(a.date)));
    setEditingMaintenanceId(null);
    setNewMaintenance({ date: '', mileage: '', repair: '', cost: '' });
  };

  const handleDeleteMaintenance = (id) => {
    setMaintenanceLog(prev => prev.filter(entry => entry.id !== id));
  };

  const handleEditMaintenance = (entry) => {
    setEditingMaintenanceId(entry.id);
    setNewMaintenance({
      date: entry.date,
      mileage: entry.mileage?.toString() || '',
      repair: entry.repair,
      cost: entry.cost?.toString() || ''
    });
  };

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const handleSave = async (shouldClose = true) => {
    if (saving) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const updatedVehicle = {
        ...editForm,
        photos: photos,
        documents: documents,
        maintenanceLog: maintenanceLog,
        currentPrice: editForm.shares || 0,
        shares: 1
      };
      await onSave(updatedVehicle, shouldClose);
      if (!shouldClose) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (e) => {
    e.stopPropagation();
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingDoc(true);
    
    for (const file of files) {
      try {
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          alert(`Invalid file type for ${file.name}. Please upload PDF or image files.`);
          continue;
        }
        
        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload to Cloudinary via backend
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setDocuments(prev => [...prev, {
            id: result.public_id || Date.now() + Math.random(),
            url: result.url,
            public_id: result.public_id,
            name: file.name,
            type: file.type,
            uploadedAt: new Date().toISOString()
          }]);
        } else {
          const error = await response.json();
          console.error('Upload failed:', error.detail);
          alert(`Failed to upload ${file.name}: ${error.detail}`);
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        alert(`Error uploading ${file.name}`);
      }
    }
    
    setUploadingDoc(false);
    
    if (docInputRef.current) {
      docInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    const doc = documents.find(d => d.id === docId);
    
    if (doc?.public_id) {
      try {
        await fetch(`${API_URL}/api/upload/image/${encodeURIComponent(doc.public_id)}`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Error deleting from Cloudinary:', error);
      }
    }
    
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const handlePhotoUpload = async (e) => {
    e.stopPropagation();
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload to Cloudinary via backend
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setPhotos(prev => [...prev, {
            id: result.public_id || Date.now() + Math.random(),
            url: result.url,
            public_id: result.public_id,
            name: file.name,
            uploadedAt: new Date().toISOString()
          }]);
        } else {
          const error = await response.json();
          console.error('Upload failed:', error.detail);
          alert(`Failed to upload ${file.name}: ${error.detail}`);
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert(`Error uploading ${file.name}`);
      }
    }
    
    setUploading(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    // Find the photo to get its public_id
    const photo = photos.find(p => p.id === photoId);
    
    // If the photo has a Cloudinary public_id, delete from Cloudinary
    if (photo?.public_id) {
      try {
        await fetch(`${API_URL}/api/upload/image/${encodeURIComponent(photo.public_id)}`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Error deleting from Cloudinary:', error);
        // Continue to remove from local state even if Cloudinary delete fails
      }
    }
    
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const goToPrevPhoto = (e) => {
    e.stopPropagation();
    if (lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  const goToNextPhoto = (e) => {
    e.stopPropagation();
    if (lightboxIndex < photos.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  // Handle keyboard navigation for lightbox
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
      if (e.key === 'ArrowRight' && lightboxIndex < photos.length - 1) setLightboxIndex(lightboxIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, photos.length]);

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < vehicles.length - 1;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Close modal only when clicking the backdrop itself
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with navigation */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isNewItem && (
            <button 
              onClick={() => canGoPrev && onNavigate(currentIndex - 1)}
              disabled={!canGoPrev}
              className={`p-2 rounded-full ${canGoPrev ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            )}
            <div>
              <h2 className="text-xl font-bold">
                {isNewItem ? 'Add New Vehicle' : `${editForm.vehicleYear} ${editForm.vehicleMake} ${editForm.vehicleModel}`}
              </h2>
              {!isNewItem && (
              <p className="text-sm text-white/80">
                Vehicle {currentIndex + 1} of {vehicles.length}
              </p>
              )}
            </div>
            {!isNewItem && (
            <button 
              onClick={() => canGoNext && onNavigate(currentIndex + 1)}
              disabled={!canGoNext}
              className={`p-2 rounded-full ${canGoNext ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Sold/Disposed Toggle */}
            {!isNewItem && (
            <button
              type="button"
              data-testid="disposed-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditForm(prev => ({ ...prev, disposed: !prev.disposed }));
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                editForm.disposed
                  ? 'bg-red-500 text-white hover:bg-red-400'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title={editForm.disposed ? 'Mark as active' : 'Mark as sold/disposed'}
            >
              <Ban className="w-4 h-4" />
              <span>{editForm.disposed ? 'Sold' : 'Active'}</span>
            </button>
            )}
            {/* Complete Toggle Button */}
            {!isNewItem && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditForm(prev => ({ ...prev, isComplete: !prev.isComplete }));
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                editForm.isComplete
                  ? 'bg-amber-400 text-amber-900 hover:bg-amber-300'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title={editForm.isComplete ? 'Mark as incomplete' : 'Mark as complete'}
            >
              <Star className={`w-4 h-4 ${editForm.isComplete ? 'fill-amber-900' : ''}`} />
              <span>{editForm.isComplete ? 'Complete' : 'Incomplete'}</span>
            </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 ${editForm.disposed ? 'bg-gray-100 asset-sold' : ''}`}>
          {/* Summary Row */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-sm text-slate-500">Purchase Price</span>
                <p className="text-lg font-semibold text-slate-700">{formatCurrency(displayCost)}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500">Current Value</span>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(displayValue)}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500">{gainLoss >= 0 ? 'Appreciation' : 'Depreciation'}</span>
                <p className={`text-lg font-semibold ${gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)} ({gainLossPercent.toFixed(1)}%)
                </p>
              </div>
            </div>
          </div>

          {/* Photo Montage Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Image className="w-5 h-5" />
                Photo Gallery
              </h3>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Upload className="w-4 h-4 mr-1" />
                {uploading ? 'Uploading...' : 'Upload Photos'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            {photos.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No photos yet</p>
                <p className="text-sm text-slate-400">Click &quot;Upload Photos&quot; to add images of your vehicle</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-4">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id} 
                    className="relative group aspect-square cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] bg-slate-100 border border-slate-200"
                    onClick={() => openLightbox(index)}
                  >
                    <img 
                      src={photo.url} 
                      alt={photo.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="45" text-anchor="middle" fill="%2394a3b8" font-size="10" font-family="sans-serif">Image not</text><text x="50" y="58" text-anchor="middle" fill="%2394a3b8" font-size="10" font-family="sans-serif">available</text></svg>';
                        e.target.className = 'w-full h-full object-contain p-4';
                      }}
                    />
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {/* Expand icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="p-3 rounded-full bg-white/90 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                        <Maximize2 className="w-5 h-5 text-slate-700" />
                      </div>
                    </div>
                    {/* Photo number badge */}
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 rounded-full text-xs text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {index + 1} / {photos.length}
                    </div>
                    {/* Delete button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg transform hover:scale-110 z-10"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Photo Lightbox Modal */}
            {lightboxIndex !== null && photos[lightboxIndex] && (
              <div 
                className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center"
                onClick={closeLightbox}
              >
                {/* Close button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
                >
                  <X className="w-8 h-8" />
                </button>

                {/* Photo counter */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full">
                  {lightboxIndex + 1} / {photos.length}
                </div>

                {/* Previous button */}
                <button
                  onClick={goToPrevPhoto}
                  disabled={lightboxIndex === 0}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10 ${
                    lightboxIndex === 0 
                      ? 'text-white/30 cursor-not-allowed' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <ChevronLeft className="w-10 h-10" />
                </button>

                {/* Image */}
                <div 
                  className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={photos[lightboxIndex].url}
                    alt={photos[lightboxIndex].name}
                    className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 150"><rect fill="%23374151" width="200" height="150"/><text x="100" y="70" text-anchor="middle" fill="%239ca3af" font-size="14" font-family="sans-serif">Image not available</text><text x="100" y="90" text-anchor="middle" fill="%236b7280" font-size="10" font-family="sans-serif">File may have been deleted</text></svg>';
                    }}
                  />
                </div>

                {/* Next button */}
                <button
                  onClick={goToNextPhoto}
                  disabled={lightboxIndex === photos.length - 1}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10 ${
                    lightboxIndex === photos.length - 1 
                      ? 'text-white/30 cursor-not-allowed' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <ChevronRight className="w-10 h-10" />
                </button>

                {/* Thumbnail strip at bottom */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg max-w-[90vw] overflow-x-auto">
                  {photos.map((photo, idx) => (
                    <button
                      key={photo.id}
                      onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                      className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                        idx === lightboxIndex ? 'border-white scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={photo.url} 
                        alt={photo.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect fill="%23374151" width="64" height="64"/><text x="32" y="36" text-anchor="middle" fill="%239ca3af" font-size="8" font-family="sans-serif">N/A</text></svg>';
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Vehicle Details Form */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button 
              onClick={() => setDetailsExpanded(!detailsExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Car className="w-5 h-5 text-orange-500" />
                Vehicle Details
              </h3>
              <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${detailsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {detailsExpanded && (
              <div className="p-4 grid grid-cols-3 gap-x-4 gap-y-2">
                {config.fields.map((field) => {
                  const isCurrencyField = field.isCurrency || field.key === 'costBasis' || field.key === 'shares';
                  const isNotesField = field.type === 'textarea';
                  
                  return (
                    <div key={field.key} className={isNotesField ? 'col-span-3 mt-2' : ''}>
                      <label className="text-xs text-slate-500 block">{field.label}</label>
                      {field.type === 'select' ? (
                        <select
                          value={editForm[field.key] || ''}
                          onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
                        >
                          <option value="">Select...</option>
                          {field.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          value={editForm[field.key] || ''}
                          onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                          placeholder={field.label}
                          className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white min-h-[80px] resize-y"
                          rows={3}
                        />
                      ) : isCurrencyField ? (
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                          <Input
                            type="number"
                            value={editForm[field.key] || ''}
                            onChange={(e) => setEditForm({ 
                              ...editForm, 
                              [field.key]: parseFloat(e.target.value) || 0
                            })}
                            placeholder="0.00"
                            className="text-sm pl-6 h-8"
                          />
                        </div>
                      ) : (
                        <Input
                          type={field.type}
                          value={editForm[field.key] || ''}
                          onChange={(e) => setEditForm({ 
                            ...editForm, 
                            [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value 
                          })}
                          placeholder={field.label}
                          className="text-sm h-8"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
            <button 
              onClick={() => setDocumentsExpanded(!documentsExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-500" />
                Purchase Documents
                {documents.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">{documents.length}</span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {documentsExpanded && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); docInputRef.current?.click(); }}
                    disabled={uploadingDoc}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    {uploadingDoc ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </Button>
                )}
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${documentsExpanded ? 'rotate-180' : ''}`} />
              </div>
            </button>
            <input
              ref={docInputRef}
              type="file"
              multiple
              accept=".pdf,image/*"
              onChange={handleDocumentUpload}
              className="hidden"
            />
            {documentsExpanded && (
              <div className="p-4">
                {documents.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-slate-500 text-sm">No documents yet</p>
                    <p className="text-xs text-slate-400">Upload purchase documents, window stickers, or titles</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`p-2 rounded-lg ${doc.type === 'application/pdf' ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {doc.type === 'application/pdf' ? (
                              <FileText className="w-5 h-5 text-red-600" />
                            ) : (
                              <Image className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-700 truncate">{doc.name}</p>
                            <p className="text-xs text-slate-400">
                              Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          <button
                            onClick={() => setViewingDocument(doc)}
                            className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={doc.name || 'document'}
                            className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Download Document"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Document Viewer Modal - Uses iframe for PDFs */}
            {viewingDocument && (
              <div 
                className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4"
                onClick={() => setViewingDocument(null)}
              >
                {viewingDocument.type === 'application/pdf' || viewingDocument.name?.toLowerCase().endsWith('.pdf') ? (
                  /* PDF Viewer - Using iframe */
                  <div className="bg-white rounded-xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-100">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{viewingDocument.name}</h3>
                          <p className="text-xs text-slate-500">
                            Uploaded {new Date(viewingDocument.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={viewingDocument.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Open in New Tab"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                        <a
                          href={viewingDocument.url}
                          download={viewingDocument.name || 'document'}
                          className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </a>
                        <button
                          onClick={() => setViewingDocument(null)}
                          className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Close"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    {/* PDF Content - Using iframe with backend proxy for correct Content-Type */}
                    <div className="flex-1 bg-slate-100 rounded-b-xl overflow-hidden relative">
                      <iframe
                        src={`${API_URL}/api/pdf-proxy?url=${encodeURIComponent(viewingDocument.url)}`}
                        className="w-full h-full border-0"
                        title={viewingDocument.name}
                        onError={() => console.log('PDF iframe error')}
                      />
                      {/* Fallback link shown at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 bg-slate-800/90 text-white text-xs p-2">
                        <div className="flex items-center justify-between">
                          <span>If PDF doesn&apos;t load:</span>
                          <a 
                            href={viewingDocument.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-300 hover:text-purple-200 underline"
                          >
                            Open directly →
                          </a>
                        </div>
                        <div className="mt-1 text-slate-400 truncate text-[10px]" title={viewingDocument.url}>
                          URL: {viewingDocument.url?.substring(0, 80)}...
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Image Viewer */
                  <div 
                    className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Image className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{viewingDocument.name}</h3>
                          <p className="text-xs text-slate-500">
                            Uploaded {new Date(viewingDocument.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={viewingDocument.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Open in New Tab"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                        <a
                          href={viewingDocument.url}
                          download={viewingDocument.name || 'document'}
                          className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </a>
                        <button
                          onClick={() => setViewingDocument(null)}
                          className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Close"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Image Content */}
                    <div className="flex-1 bg-slate-100 rounded-b-xl overflow-hidden">
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <img
                          src={viewingDocument.url}
                          alt={viewingDocument.name}
                          className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Maintenance Log Section */}
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
            <button 
              onClick={() => setMaintenanceExpanded(!maintenanceExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Maintenance Log
                {maintenanceLog.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{maintenanceLog.length}</span>
                )}
              </h3>
              <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${maintenanceExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {maintenanceExpanded && (
              <div className="p-4">
                {/* Add/Edit Maintenance Form */}
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Date</label>
                      <Input
                        type="date"
                        value={newMaintenance.date}
                        onChange={(e) => setNewMaintenance({ ...newMaintenance, date: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Mileage</label>
                      <Input
                        type="number"
                        value={newMaintenance.mileage}
                        onChange={(e) => setNewMaintenance({ ...newMaintenance, mileage: e.target.value })}
                        placeholder="e.g. 25000"
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500 mb-1 block">Repair/Service</label>
                      <Input
                        type="text"
                        value={newMaintenance.repair}
                        onChange={(e) => setNewMaintenance({ ...newMaintenance, repair: e.target.value })}
                        placeholder="e.g. Oil change, Tire rotation"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Cost</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                      <Input
                        type="number"
                        value={newMaintenance.cost}
                        onChange={(e) => setNewMaintenance({ ...newMaintenance, cost: e.target.value })}
                        placeholder="0.00"
                        className="text-sm pl-7"
                      />
                    </div>
                    {editingMaintenanceId ? (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateMaintenance(editingMaintenanceId)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white px-2"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingMaintenanceId(null);
                            setNewMaintenance({ date: '', mileage: '', repair: '', cost: '' });
                          }}
                          className="px-2"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={handleAddMaintenance}
                        disabled={!newMaintenance.date}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-3"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Maintenance Log Table */}
            {maintenanceLog.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">No maintenance records yet</p>
                <p className="text-sm text-slate-400">Add service records to track your vehicle&apos;s maintenance history</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[100px_80px_1fr_90px_60px] gap-3 bg-slate-100 px-4 py-2 text-xs font-medium text-slate-500 uppercase">
                  <div>Date</div>
                  <div className="text-right">Mileage</div>
                  <div>Repair/Service</div>
                  <div className="text-right">Cost</div>
                  <div></div>
                </div>
                
                {/* Table Body */}
                <div className="divide-y divide-slate-100">
                  {maintenanceLog.map((entry) => (
                    <div key={entry.id} className="grid grid-cols-[100px_80px_1fr_90px_60px] gap-3 px-4 py-2 text-sm items-center hover:bg-slate-50">
                      <div className="text-slate-600">
                        {new Date(entry.date).toLocaleDateString()}
                      </div>
                      <div className="text-right text-slate-600">
                        {entry.mileage ? entry.mileage.toLocaleString() : '—'}
                      </div>
                      <div className="text-slate-800">{entry.repair}</div>
                      <div className="text-right font-medium text-slate-700">
                        {entry.cost ? formatCurrency(entry.cost) : '—'}
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEditMaintenance(entry)}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteMaintenance(entry.id)}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total Row */}
                <div className="grid grid-cols-[100px_80px_1fr_90px_60px] gap-3 bg-slate-50 px-4 py-2 text-sm border-t border-slate-200">
                  <div className="font-medium text-slate-600">Total</div>
                  <div></div>
                  <div className="text-slate-500">{maintenanceLog.length} record{maintenanceLog.length !== 1 ? 's' : ''}</div>
                  <div className="text-right font-bold text-slate-800">
                    {formatCurrency(maintenanceLog.reduce((sum, entry) => sum + (entry.cost || 0), 0))}
                  </div>
                  <div></div>
                </div>
              </div>
            )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3 bg-slate-50">
          {saveSuccess && (
            <span className="flex items-center text-sm text-emerald-600 mr-auto">
              <Check className="w-4 h-4 mr-1" /> Saved
            </span>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleSave(false)} 
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={() => handleSave(true)} className="bg-orange-500 hover:bg-orange-600 text-white" disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save & Close'}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default VehicleDetailModal;
